import { useContext, useMemo, useRef, useState } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Button,
  Input,
  FormControl,
  FormLabel,
  Checkbox,
  Select,
} from "@chakra-ui/react";

import { OptionBase, PropsValue, CreatableSelect } from "chakra-react-select";

import { useTenant } from "modules/tenant/providers/tenant-provider";
import { selectChakraStyles } from "utils/constants";
import MembersField from "components/elements/MembersField";
import { SelectOption } from "utils/types";
import { apiRequest } from "utils/api";
import { AlertsContext } from "providers/alerts";
import colors from "utils/colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { useRoles } from "hooks/useRoles";
import { useMutation } from "@tanstack/react-query";
import { getErrorMessage } from "utils/errorHandler";
import { useEntitiesForTheCompany } from "hooks/useEntitiesForTheCompany";
import LocationAutocomplete from "components/elements/LocationAutocomplete";
import { Json } from "types/supabase";
import { BorrowerCompanySelect } from "components/loans/CreateNewLoanApplication/components/BorrowerCompanySelect";
import { useBorrowerCompaniesForTheTenant } from "hooks/useBorrowerCompaniesForTheTenant";

interface DrawerFormFieldProps {
  label: string;
  placeholder: string;
  value?: string;
  onChange: (val: string) => void;
  type?: string;
  cy?: string;
  disabled?: boolean;
  isRequired?: boolean;
}

export const DrawerFormField = ({
  label,
  placeholder,
  value,
  onChange,
  type = "text",
  cy,
  disabled,
  isRequired = true,
}: DrawerFormFieldProps) => (
  <FormControl mb="16px" isRequired={isRequired}>
    <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
      {label}
    </FormLabel>
    <Input
      data-cy={cy}
      type={type}
      placeholder={placeholder}
      disabled={disabled}
      value={value}
      variant="primary"
      onChange={ev => onChange(ev.target.value)}
    />
  </FormControl>
);

interface CompanyOption extends OptionBase {
  label: string;
  value: string;
  address: string | Json | null;
}

export default function AddBorrower({ onLoad }: { onLoad: () => void }) {
  const { user } = useTenant();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  return (
    <>
      <Button
        data-cy="add-borrower-btn"
        ref={btnRef}
        variant={getCustomButtonVariant(user)}
        leftIcon={<FontAwesomeIcon icon={["far", "plus"]} />}
        onClick={onOpen}
      >
        Add Borrower
      </Button>
      {isOpen && (
        <AddBorrowerDrawer isOpen={isOpen} onClose={onClose} btnRef={btnRef} onLoad={onLoad} />
      )}
    </>
  );
}

function AddBorrowerDrawer({
  isOpen,
  onClose,
  btnRef,
  onLoad,
}: {
  isOpen: boolean;
  onClose: () => void;
  btnRef: React.RefObject<any>;
  onLoad: () => void;
}) {
  const { showSuccess, showError } = useContext(AlertsContext);
  const { user } = useTenant();
  const { data: borrowerCompanies } = useBorrowerCompaniesForTheTenant();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyAddress, setCompanyAddress] = useState<string>();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<PropsValue<CompanyOption>>();
  const [sendInviteEmail, setSendInviteEmail] = useState(false);
  const [entity, setEntity] = useState<string>();
  const [members, setMembers] = useState<SelectOption[]>([
    {
      label: `${user.first_name} ${user.last_name}`,
      value: user.id,
    },
  ]);
  const [jobTitle, setJobTitle] = useState("");

  const [borrowerCompanyId, setBorrowerCompanyId] = useState<string | undefined>();

  const { data: entities } = useEntitiesForTheCompany(borrowerCompanyId);

  const { roles: borrowerRoles, isLoading: isLoadingRoles } = useRoles({
    company_id: user?.company?.id,
    userType: "borrower",
  });

  const borrowerRoleOptions = useMemo(
    () =>
      borrowerRoles?.map((role: any) => ({
        label: role.name,
        value: role.id,
      })) ?? [],
    [borrowerRoles]
  );

  const { mutate: sendInvite, isPending: isSendingInvite } = useMutation({
    mutationFn: async () => {
      if (!firstName || !lastName || !email || !borrowerCompanyId || !role) {
        throw new Error("Please, fill in all required fields.");
      }

      await apiRequest("/supabase/invite", {
        first_name: firstName,
        last_name: lastName,
        email,
        company: borrowerCompanyId,
        company_address: companyAddress,
        entity: entity,
        role: (role as CompanyOption).value,
        type: "borrower",
        contacts: members.map(member => member.value),
        send_invite_email: sendInviteEmail,
        job_title: jobTitle,
      });
    },
    onSuccess: () => {
      onClose();
      onLoad();
      setFirstName("");
      setLastName("");
      setEmail("");
      setBorrowerCompanyId(undefined);
      setMembers([
        {
          label: `${user.first_name} ${user.last_name}`,
          value: user.id,
        },
      ]);
      setJobTitle("");
      showSuccess({
        title: "Account Created",
        description: "The borrower account has been created.",
      });
    },
    onError: err => {
      showError({
        title: "Creation Failed",
        description: getErrorMessage(err),
      });
    },
  });

  function handleCompanyChange(newValue: string | undefined) {
    setEntity(undefined);

    if (!newValue) {
      setCompanyAddress(undefined);
      setBorrowerCompanyId(undefined);
      return;
    }

    const address = borrowerCompanies?.find(company => company.id === newValue)?.address;
    setCompanyAddress(address ?? undefined);
    setBorrowerCompanyId(newValue);
  }

  const loading = isLoadingRoles;

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} finalFocusRef={btnRef}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Add Borrower</DrawerHeader>
        <DrawerBody>
          <DrawerFormField
            label="First Name"
            placeholder="John"
            value={firstName}
            onChange={setFirstName}
            cy="invite-borrower-first-name"
          />
          <DrawerFormField
            label="Last Name"
            placeholder="Doe"
            value={lastName}
            onChange={setLastName}
            cy="invite-borrower-last-name"
          />
          <DrawerFormField
            label="Email Address"
            placeholder="john.doe@example.com"
            value={email}
            onChange={setEmail}
            type="email"
            cy="invite-borrower-email"
          />
          <DrawerFormField
            label="Job Title"
            placeholder="Chief Lendtech Officer"
            value={jobTitle}
            onChange={setJobTitle}
            type="text"
            cy="invite-borrower-job-title"
            isRequired={false}
          />
          <FormControl mb="16px" isRequired>
            <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
              Company
            </FormLabel>
            <BorrowerCompanySelect
              borrowerCompanyId={borrowerCompanyId}
              setBorrowerCompanyId={handleCompanyChange}
            />
          </FormControl>
          {borrowerCompanyId && (
            <FormControl mb="16px">
              <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
                Company Address
              </FormLabel>
              <LocationAutocomplete onChange={setCompanyAddress} defaultValue={companyAddress} />
            </FormControl>
          )}
          {entities && entities.length > 0 && (
            <FormControl>
              <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
                Entity
              </FormLabel>
              <Select
                placeholder="Select an entity"
                value={entity}
                onChange={ev => setEntity(ev.target.value)}
              >
                {entities.map(entity => (
                  <option value={entity.id} key={entity.id}>
                    {entity.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl mb="16px" isRequired>
            <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
              Role
            </FormLabel>
            <CreatableSelect
              className="borrower-role-select"
              useBasicStyles
              selectedOptionColor="black"
              colorScheme="purple"
              variant="outline"
              chakraStyles={selectChakraStyles}
              value={role}
              hideSelectedOptions
              focusBorderColor={colors.GRAY_50}
              onChange={(val: any) => setRole(val)}
              options={borrowerRoleOptions}
              isLoading={loading}
              noOptionsMessage={() => "No roles found for company."}
            />
          </FormControl>
          <FormControl>
            <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
              Borrower Contacts
            </FormLabel>
            <MembersField
              members={members}
              setMembers={setMembers}
              isMulti
              exclude={[]}
              types={["lender"]}
              isRequired={false}
            />
          </FormControl>
          <FormControl mt="16px">
            <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
              Invite user to the platform
            </FormLabel>
            <Checkbox
              isChecked={sendInviteEmail}
              onChange={ev => setSendInviteEmail(ev.target.checked)}
            >
              Send an email invitation
            </Checkbox>
          </FormControl>
        </DrawerBody>
        <DrawerFooter gap={2} justifyContent="space-between">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            flex="1"
            leftIcon={<FontAwesomeIcon icon={["far", "plus"]} />}
            onClick={() => sendInvite()}
            data-cy="invite-borrower-send-btn"
            variant={getCustomButtonVariant(user)}
            isLoading={isSendingInvite}
            loadingText="Adding..."
          >
            Add Account
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
